import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  Organization,
  OrganizationGroup,
  UpdateUserRequest,
  User,
  UsersService,
} from '../../../shared/services/users/users.service';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';
import { UserRole, UserRoleService } from '../../../shared/services/user-role/user-role.service';
import { ApiService, HttpMethod } from '../../../shared/services/api/api.service';

interface PersonalDraft {
  FIRST_NAME: string;
  LAST_NAME: string;
  EMAIL: string;
}

interface OrgDraft {
  ROLE: string;
  ORGANIZATION_GROUP_ID: number | null;
  ORGANIZATION_ID: number | null;
}

@Component({
  selector: 'app-user-detail-general',
  templateUrl: './user-detail-general.component.html',
  styleUrls: ['./user-detail-general.component.scss'],
})
export class UserDetailGeneralComponent implements OnInit, OnDestroy {
  @Input() user!: User;

  public editingPersonal = false;
  public personalDraft: PersonalDraft = { FIRST_NAME: '', LAST_NAME: '', EMAIL: '' };

  public editingOrganization = false;
  public orgDraft: OrgDraft = { ROLE: '', ORGANIZATION_GROUP_ID: null, ORGANIZATION_ID: null };
  public organizationGroups: OrganizationGroup[] = [];
  public filteredOrgs: Organization[] = [];
  public displayOrgs: Organization[] = [];
  public loadingOrgs = false;
  public isSaving = false;

  public readonly allRoleOptions = ['SUPER_ADMIN', 'SUPERVISOR', 'MANAGER', 'ADVISOR'];
  public readonly limitedRoleOptions = ['MANAGER', 'ADVISOR'];

  private currentUser: User | null = null;
  private subscriptions = new Subscription();

  get isSuperAdmin(): boolean { return this.userRoleService.isSuperAdmin(); }
  get isSupervisor(): boolean { return this.userRoleService.isSupervisor(); }
  get isManager(): boolean { return this.userRoleService.isManager(); }

  /**
   * Super admin: any user.
   * Supervisor: any user whose org is in their org group (backend enforces scope).
   * Manager: only advisors in their own organization.
   */
  get canEditPersonalInfo(): boolean {
    if (this.isSuperAdmin || this.isSupervisor) return true;
    if (this.isManager) {
      return (
        this.user?.ROLE === (UserRole.ADVISOR as string) &&
        this.user?.ORGANIZATION_ID === this.currentUser?.ORGANIZATION_ID
      );
    }
    return false;
  }

  get canEditOrganizationSection(): boolean {
    return this.canEditPersonalInfo;
  }

  /** Only super admin may reassign the org group a user belongs to. */
  get canEditOrgGroup(): boolean {
    return this.isSuperAdmin;
  }

  /** Super admin and supervisor may reassign a user's organization within their scope. */
  get canEditOrg(): boolean {
    return this.isSuperAdmin || this.isSupervisor;
  }

  /** Super admin sees all roles; supervisor and manager are limited to ADVISOR/MANAGER. */
  get availableRoleOptions(): string[] {
    return this.isSuperAdmin ? this.allRoleOptions : this.limitedRoleOptions;
  }

  constructor(
    private readonly userService: UsersService,
    private readonly toaster: ToasterService,
    private readonly userRoleService: UserRoleService,
    private readonly apiService: ApiService,
  ) {}

  get displayOrgGroupName(): string {
    if (this.user?.ORGANIZATION_GROUP_NAME) return this.user.ORGANIZATION_GROUP_NAME;
    if (!this.user?.ORGANIZATION_GROUP_ID) return '—';
    const g = this.organizationGroups.find(x => x.GROUP_ID === this.user.ORGANIZATION_GROUP_ID);
    return g?.GROUP_NAME ?? String(this.user.ORGANIZATION_GROUP_ID);
  }

  get displayOrgName(): string {
    if (this.user?.ORGANIZATION_NAME) return this.user.ORGANIZATION_NAME;
    if (!this.user?.ORGANIZATION_ID) return '—';
    const org = this.displayOrgs.find(x => x.ORGANIZATION_ID === this.user.ORGANIZATION_ID);
    return org?.ORGANIZATION_NAME ?? String(this.user.ORGANIZATION_ID);
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.userRoleService.currentUser$.subscribe((u) => { this.currentUser = u; }),
    );
    if (this.isSuperAdmin) {
      void this.loadOrganizationGroups();
    }
    if (!this.user?.ORGANIZATION_GROUP_NAME && this.user?.ORGANIZATION_GROUP_ID != null) {
      void this.loadDisplayOrgs(this.user.ORGANIZATION_GROUP_ID);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // ── Personal information ──────────────────────────────────────────────────

  public startEditPersonal(): void {
    if (!this.user || !this.canEditPersonalInfo) return;
    this.personalDraft = {
      FIRST_NAME: this.user.FIRST_NAME ?? '',
      LAST_NAME: this.user.LAST_NAME ?? '',
      EMAIL: this.user.EMAIL ?? '',
    };
    this.editingPersonal = true;
  }

  public cancelEditPersonal(): void {
    this.editingPersonal = false;
  }

  public async savePersonal(): Promise<void> {
    if (!this.user || this.isSaving || !this.canEditPersonalInfo) return;

    const dirty =
      this.personalDraft.FIRST_NAME !== (this.user.FIRST_NAME ?? '') ||
      this.personalDraft.LAST_NAME !== (this.user.LAST_NAME ?? '') ||
      this.personalDraft.EMAIL !== (this.user.EMAIL ?? '');

    if (!dirty) {
      this.editingPersonal = false;
      return;
    }

    const payload: UpdateUserRequest = {
      FIRST_NAME: this.personalDraft.FIRST_NAME,
      LAST_NAME: this.personalDraft.LAST_NAME,
      EMAIL: this.personalDraft.EMAIL,
      ORGANIZATION_ID: this.user.ORGANIZATION_ID,
      ROLE: this.user.ROLE,
    };

    await this.submitUpdate(payload, () => {
      this.user.FIRST_NAME = this.personalDraft.FIRST_NAME;
      this.user.LAST_NAME = this.personalDraft.LAST_NAME;
      this.user.EMAIL = this.personalDraft.EMAIL;
      this.editingPersonal = false;
    });
  }

  // ── Organization information ──────────────────────────────────────────────

  private async loadOrganizationGroups(): Promise<void> {
    try {
      const res = await this.apiService.call<{ data: OrganizationGroup[] }>(
        HttpMethod.GET,
        'organization-groups?pageSize=100',
      );
      this.organizationGroups = res?.data ?? [];
    } catch (error) {
      console.error('Error loading organization groups:', error);
    }
  }

  private async loadDisplayOrgs(groupId: number): Promise<void> {
    try {
      const res = await this.apiService.call<{ data: Organization[] }>(
        HttpMethod.GET,
        `organization-groups/${groupId}/organizations?pageSize=100`,
      );
      this.displayOrgs = res?.data ?? [];
    } catch (error) {
      console.error('Error loading display organizations:', error);
    }
  }

  private async loadOrgsForGroup(groupId: number): Promise<void> {
    this.loadingOrgs = true;
    try {
      const res = await this.apiService.call<{ data: Organization[] }>(
        HttpMethod.GET,
        `organization-groups/${groupId}/organizations?pageSize=100`,
      );
      this.filteredOrgs = res?.data ?? [];
    } catch (error) {
      console.error('Error loading organizations for group:', error);
      this.filteredOrgs = [];
    } finally {
      this.loadingOrgs = false;
    }
  }

  public startEditOrganization(): void {
    if (!this.user || !this.canEditOrganizationSection) return;

    // Super admin: start from the target user's current group.
    // Supervisor/manager: their own group (immutable for this editor).
    const groupId = this.isSuperAdmin
      ? (this.user.ORGANIZATION_GROUP_ID ?? null)
      : (this.currentUser?.ORGANIZATION_GROUP_ID ?? null);

    this.orgDraft = {
      ROLE: this.user.ROLE ?? '',
      ORGANIZATION_GROUP_ID: groupId,
      ORGANIZATION_ID: this.user.ORGANIZATION_ID ?? null,
    };

    this.filteredOrgs = [];
    if (this.canEditOrg && groupId != null) {
      void this.loadOrgsForGroup(groupId);
    }

    this.editingOrganization = true;
  }

  public onOrgGroupChange(): void {
    this.orgDraft.ORGANIZATION_ID = null;
    this.filteredOrgs = [];
    if (this.orgDraft.ORGANIZATION_GROUP_ID != null) {
      void this.loadOrgsForGroup(this.orgDraft.ORGANIZATION_GROUP_ID);
    }
  }

  public cancelEditOrganization(): void {
    this.editingOrganization = false;
  }

  public async saveOrganization(): Promise<void> {
    if (!this.user || this.isSaving || !this.canEditOrganizationSection) return;

    const roleChanged = this.orgDraft.ROLE !== (this.user.ROLE ?? '');
    const orgChanged = this.orgDraft.ORGANIZATION_ID !== (this.user.ORGANIZATION_ID ?? null);

    if (!roleChanged && !orgChanged) {
      this.editingOrganization = false;
      this.toaster.info('No changes to save');
      return;
    }

    if (this.canEditOrg && !this.orgDraft.ORGANIZATION_ID) {
      this.toaster.warning('Organization is required');
      return;
    }

    const payload: UpdateUserRequest = {
      FIRST_NAME: this.user.FIRST_NAME,
      LAST_NAME: this.user.LAST_NAME,
      EMAIL: this.user.EMAIL,
      ROLE: this.orgDraft.ROLE,
      ORGANIZATION_ID: this.orgDraft.ORGANIZATION_ID ?? this.user.ORGANIZATION_ID,
    };

    await this.submitUpdate(payload, () => {
      this.user.ROLE = payload.ROLE;
      this.user.ORGANIZATION_ID = payload.ORGANIZATION_ID ?? this.user.ORGANIZATION_ID;
      if (this.orgDraft.ORGANIZATION_GROUP_ID != null) {
        this.user.ORGANIZATION_GROUP_ID = this.orgDraft.ORGANIZATION_GROUP_ID;
      }
      this.editingOrganization = false;
    });
  }

  // ── Shared ────────────────────────────────────────────────────────────────

  private async submitUpdate(
    payload: UpdateUserRequest,
    onSuccess: () => void,
  ): Promise<void> {
    this.isSaving = true;
    this.toaster.info('Saving changes...');
    try {
      await this.userService.updateUser(this.user.USER_ID, payload);
      onSuccess();
      this.toaster.success('Changes saved successfully');
    } catch (error: unknown) {
      console.error('Error saving changes:', error);
      this.toaster.warning('Failed to save changes');
    } finally {
      this.isSaving = false;
    }
  }

  public formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
